import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth, useIsMobile } from './hooks';
import { Audiencias } from './pages/Audiencias';
import { Reconciliacao } from './pages/Reconciliacao';
import { KanbanMensal } from './pages/KanbanMensal';
import { KanbanSemanal } from './pages/KanbanSemanal';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { MinhaCaixa } from './pages/MinhaCaixa';
import { Mobile } from './pages/Mobile';
import { TrocarSenha } from './pages/TrocarSenha';
import { Usuarios } from './pages/Usuarios';
import { Clientes } from './pages/Clientes';
import { ClientesBranded } from './pages/ClientesBranded';
import { Plano } from './pages/Plano';
import { AdminEscritorios } from './pages/AdminEscritorios';
import { Administracao } from './pages/Administracao';
import { Seguranca } from './pages/Seguranca';
import { PeticoesIniciais } from './pages/PeticoesIniciais';
import { PeticionarComModelo } from './pages/PeticionarComModelo';
import { Financeiro } from './pages/Financeiro';
import { Jurisprudencia } from './pages/Jurisprudencia';
import { Custos } from './pages/Custos';

/** Wrapper que redireciona pra login se não autenticado, e pra Mobile se for tela pequena. */
function Protegida({ children }: { children: React.ReactNode }) {
  const { login } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (!login) {
      navigate('/login', { replace: true });
      return;
    }
    // Troca de senha obrigatória (1º acesso) — bloqueia o app até trocar.
    if (login.precisa_trocar_senha && loc.pathname !== '/trocar-senha') {
      navigate('/trocar-senha', { replace: true });
      return;
    }

    const papel = login.papel;

    // Audiencista: apenas /audiencias e /trocar-senha
    if (papel === 'audiencista') {
      if (loc.pathname !== '/audiencias' && loc.pathname !== '/trocar-senha') {
        navigate('/audiencias', { replace: true });
        return;
      }
    }
    // Correspondente: apenas /semanal, /mobile e /trocar-senha
    else if (papel === 'correspondente') {
      if (loc.pathname !== '/semanal' && loc.pathname !== '/mobile' && loc.pathname !== '/trocar-senha') {
        navigate('/semanal', { replace: true });
        return;
      }
    }
    // Testador: bloqueia /caixa e /usuarios
    else if (papel === 'testador') {
      if (loc.pathname === '/caixa' || loc.pathname === '/usuarios') {
        navigate('/semanal', { replace: true });
        return;
      }
    }

    // Clientes (e cópia re-branded): apenas admin
    if ((loc.pathname === '/clientes' || loc.pathname === '/clientes-novo') && papel !== 'admin') {
      navigate('/semanal', { replace: true });
      return;
    }

    // Peticionar com modelo: apenas admin/gerente
    if (loc.pathname === '/peticionar-modelo' && papel !== 'admin' && papel !== 'gerente') {
      navigate('/semanal', { replace: true });
      return;
    }

    // Financeiro: apenas admin
    if (loc.pathname === '/financeiro' && papel !== 'admin') {
      navigate('/semanal', { replace: true });
      return;
    }

    // Se é mobile e estiver numa rota desktop, redireciona pra /mobile (a menos que ?force=desktop)
    // Para audiencistas, eles NÃO devem ir para /mobile (visto que /audiencias é uma pauta de audiências)
    if (papel !== 'audiencista') {
      const params = new URLSearchParams(loc.search);
      if (isMobile && params.get('force') !== 'desktop' && loc.pathname !== '/mobile') {
        navigate('/mobile', { replace: true });
      }
    }
  }, [login, isMobile, loc.pathname, loc.search, navigate]);

  if (!login) return null;

  const papel = login.papel;
  if (papel === 'audiencista' && loc.pathname !== '/audiencias' && loc.pathname !== '/trocar-senha') {
    return null;
  }
  if (papel === 'correspondente' && loc.pathname !== '/semanal' && loc.pathname !== '/mobile' && loc.pathname !== '/trocar-senha') {
    return null;
  }
  if (papel === 'testador' && (loc.pathname === '/caixa' || loc.pathname === '/usuarios')) {
    return null;
  }
  if ((loc.pathname === '/clientes' || loc.pathname === '/clientes-novo') && papel !== 'admin') {
    return null;
  }
  if (loc.pathname === '/peticionar-modelo' && papel !== 'admin' && papel !== 'gerente') {
    return null;
  }
  if (loc.pathname === '/financeiro' && papel !== 'admin') {
    return null;
  }

  return <>{children}</>;
}

/** Rota raiz: visitante deslogado vê a LANDING; logado vai pro kanban. */
function Raiz() {
  const { login } = useAuth();
  if (!login) return <Landing />;
  return <Navigate to="/semanal" replace />;
}

export function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/trocar-senha" element={<TrocarSenha />} />
        <Route path="/" element={<Raiz />} />
        <Route path="/semanal"    element={<Protegida><KanbanSemanal /></Protegida>} />
        <Route path="/mensal"     element={<Protegida><KanbanMensal /></Protegida>} />
        <Route path="/audiencias" element={<Protegida><Audiencias /></Protegida>} />
        <Route path="/reconciliacao" element={<Protegida><Reconciliacao /></Protegida>} />
        <Route path="/usuarios"   element={<Protegida><Usuarios /></Protegida>} />
        <Route path="/caixa"      element={<Protegida><MinhaCaixa /></Protegida>} />
        <Route path="/clientes"   element={<Protegida><Clientes /></Protegida>} />
        <Route path="/clientes-novo" element={<Protegida><ClientesBranded /></Protegida>} />
        <Route path="/peticoes-iniciais" element={<Protegida><PeticoesIniciais /></Protegida>} />
        <Route path="/peticionar-modelo" element={<Protegida><PeticionarComModelo /></Protegida>} />
        <Route path="/financeiro" element={<Protegida><Financeiro /></Protegida>} />
        <Route path="/jurisprudencia" element={<Protegida><Jurisprudencia /></Protegida>} />
        <Route path="/custos" element={<Protegida><Custos /></Protegida>} />
        <Route path="/plano"      element={<Protegida><Plano /></Protegida>} />
        <Route path="/admin/escritorios" element={<Protegida><AdminEscritorios /></Protegida>} />
        <Route path="/administracao" element={<Protegida><Administracao /></Protegida>} />
        <Route path="/seguranca" element={<Protegida><Seguranca /></Protegida>} />
        <Route path="/mobile"     element={<Protegida><Mobile /></Protegida>} />
        <Route path="*"        element={<Navigate to="/semanal" replace />} />
      </Routes>
    </>
  );
}
